/* Staff management: create/edit/deactivate nurse, doctor and ops accounts.
   Accounts created here are the ONLY phones that can log into /nurse, /doctor, /ops. */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { Syringe, Stethoscope, Headset, Plus, Edit2, Ban, XCircle, CheckCircle2, RefreshCw, Copy } from 'lucide-react';
import { fieldError, generatePassword, indianMobile10 } from '../../lib/validation';

const ROLES = [
  { id: 'nurse', label: 'Nurses', icon: Syringe },
  { id: 'doctor', label: 'Doctors', icon: Stethoscope },
  { id: 'ops', label: 'Ops team', icon: Headset },
];

const EMPTY = {
  role: 'nurse', full_name: '', phone: '', email: '', password: '',
  qualification: '', uniform_size: '',
  license_number: '', hospital: '', fellowship: '', address: '',
};

export default function Nurses() {
  const { getAuthHeaders } = useAuth();
  const [role, setRole] = useState('nurse');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [generatedPw, setGeneratedPw] = useState('');
  const [resettingPw, setResettingPw] = useState(false);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${config.API}/admin/staff`, { params: { role }, headers: getAuthHeaders() });
      setItems(data.items || []);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeaders, role]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY, role });
    setError('');
    setGeneratedPw('');
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      role: s.role,
      full_name: s.full_name || '',
      phone: s.phone || '',
      email: s.email || '',
      qualification: s.profile?.qualification || '',
      uniform_size: s.profile?.uniform_size || '',
      license_number: s.profile?.license_number || '',
      hospital: s.profile?.hospital || '',
      fellowship: s.profile?.fellowship || '',
      address: s.profile?.address || '',
    });
    setError('');
    setGeneratedPw('');
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = fieldError({
      name: form.full_name,
      phone: form.phone,
      email: form.email,
      password: editId || !(form.password || "").trim() ? undefined : form.password,
    });
    if (err) { setError(err); return; }
    setError('');
    const h = { headers: getAuthHeaders() };
    try {
      if (editId) {
        const body = { ...form, phone: indianMobile10(form.phone) };
        delete body.role;
        delete body.password;
        await axios.patch(`${config.API}/admin/staff/${editId}`, body, h);
        setMsg('Staff member updated');
      } else {
        const { data } = await axios.post(`${config.API}/admin/staff`, {
          ...form,
          phone: indianMobile10(form.phone),
          generate_password: !form.password,
        }, h);
        const pw = data.temporary_password || form.password;
        setGeneratedPw(pw);
        const via = [];
        if (data.notified?.emailed) via.push('email');
        if (data.notified?.smsed) via.push('SMS');
        setMsg(`${form.role} account created. Password ${via.length ? 'sent via ' + via.join(' + ') : 'is shown below'} — they can log in at /${form.role}`);
      }
      setShowForm(false);
      fetchStaff();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Save failed');
    }
  };

  const toggleActive = async (s) => {
    const h = { headers: getAuthHeaders() };
    try {
      if (s.active) {
        if (!window.confirm(`Deactivate ${s.full_name}? They will no longer be able to log in.`)) return;
        await axios.delete(`${config.API}/admin/staff/${s.id}`, h);
      } else {
        await axios.patch(`${config.API}/admin/staff/${s.id}`, { active: true }, h);
      }
      fetchStaff();
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
  };

  const cols = role === 'nurse'
    ? ['Name', 'Phone', 'Email', 'Qualification', 'Uniform', 'Status', 'Login', 'Actions']
    : role === 'doctor'
      ? ['Name', 'Phone', 'License', 'Hospital', 'Email', 'Login', 'Actions']
      : ['Name', 'Phone', 'Email', 'Login', 'Actions'];

  return (
    <div data-testid="admin-staff" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {ROLES.map((r) => (
            <button key={r.id} onClick={() => setRole(r.id)} data-testid={`staff-tab-${r.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                role === r.id ? 'bg-[#673c96] text-white' : 'bg-white border border-[#673c96]/20 text-[#673c96]'}`}>
              <r.icon className="w-4 h-4" /> {r.label}
            </button>
          ))}
        </div>
        <button onClick={openCreate} data-testid="add-staff-btn"
          className="bg-[#f37721] text-white hover:bg-[#e0661a] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add {role === 'ops' ? 'ops member' : role}
        </button>
      </div>

      {msg && <p className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {msg}</p>}
      {generatedPw && (
        <p className="text-sm text-[#311b52] bg-[#fdfcff] border border-[#673c96]/15 rounded-xl px-4 py-3 flex items-center gap-2">
          Temporary password: <code className="font-semibold">{generatedPw}</code>
          <button type="button" onClick={() => navigator.clipboard?.writeText(generatedPw)} className="text-[#673c96]"><Copy className="w-4 h-4" /></button>
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead className="bg-[#fdfcff]">
                <tr className="border-b border-[#673c96]/10">
                  {cols.map(h => <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} className={`border-b border-[#673c96]/5 hover:bg-[#fdfcff] transition-colors ${!s.active ? 'opacity-50' : ''}`}
                    data-testid={`staff-row-${s.id}`}>
                    <td className="py-3 px-4 font-medium text-[#311b52]">{s.full_name}</td>
                    <td className="py-3 px-4 text-[#3e195e]">+{s.phone}</td>
                    {role === 'nurse' && <>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.email || '—'}</td>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.profile?.qualification || '—'}</td>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.profile?.uniform_size || '—'}</td>
                      <td className="py-3 px-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        s.profile?.status === 'available' ? 'bg-green-100 text-green-800' :
                        s.profile?.status === 'on_duty' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {s.profile?.status || 'available'}</span></td>
                    </>}
                    {role === 'doctor' && <>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.profile?.license_number || '—'}</td>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.profile?.hospital || '—'}</td>
                      <td className="py-3 px-4 text-[#673c96] text-xs">{s.email || '—'}</td>
                    </>}
                    {role === 'ops' && <td className="py-3 px-4 text-[#673c96] text-xs">{s.email || '—'}</td>}
                    <td className="py-3 px-4">
                      {s.active
                        ? <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700">Can log in</span>
                        : <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700">Deactivated</span>}
                    </td>
                    <td className="py-3 px-4 flex gap-2">
                      <button onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`} className="text-[#673c96] hover:text-[#f37721]" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleActive(s)} data-testid={`toggle-staff-${s.id}`}
                        className={s.active ? 'text-red-400 hover:text-red-600' : 'text-emerald-500 hover:text-emerald-700'}
                        title={s.active ? 'Deactivate' : 'Reactivate'}>
                        {s.active ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={cols.length} className="py-10 text-center text-sm text-[#673c96]">
                    No {role} accounts yet — create one so they can log in to their portal.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()} data-testid="staff-form-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
                {editId ? 'Edit' : 'Add'} {form.role === 'ops' ? 'ops member' : form.role}
              </h3>
              <button type="button" onClick={() => setShowForm(false)}><XCircle className="w-5 h-5 text-[#673c96]" /></button>
            </div>

            <div>
              <label className="text-xs font-medium text-[#311b52] block mb-1">Full name *</label>
              <input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" data-testid="staff-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#311b52] block mb-1">Phone (used for OTP login) *</label>
              <input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                maxLength={10} placeholder="10-digit mobile" className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" data-testid="staff-phone-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#311b52] block mb-1">Email * {form.role === 'doctor' ? '(shown on prescriptions + login)' : '(required to log in)'}</label>
              <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" data-testid="staff-email-input" />
            </div>
            {!editId && (
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Password (min 8) — or generate</label>
                <div className="flex gap-2">
                  <input type="text" minLength={8} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    className="flex-1 px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" data-testid="staff-password-input" />
                  <button type="button" onClick={() => setForm({ ...form, password: generatePassword() })}
                    className="px-3 py-2 rounded-lg border border-[#673c96]/20 text-xs font-semibold text-[#673c96]">Generate</button>
                </div>
              </div>
            )}
            {editId && (
              <button type="button" disabled={resettingPw} onClick={async () => {
                setResettingPw(true);
                setError('');
                try {
                  const { data } = await axios.post(`${config.API}/admin/staff/${editId}/reset-password`, {}, { headers: getAuthHeaders() });
                  setGeneratedPw(data.temporary_password || '');
                  setMsg('New password generated. Email/SMS is sending in the background.');
                } catch (err) {
                  const d = err?.response?.data?.detail;
                  setError(typeof d === 'string' ? d : 'Could not reset password');
                } finally {
                  setResettingPw(false);
                }
              }} className="w-full inline-flex items-center justify-center gap-2 border border-[#673c96]/30 text-[#673c96] rounded-lg py-2 text-sm font-semibold disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${resettingPw ? "animate-spin" : ""}`} /> {resettingPw ? "Generating…" : "Generate & send new password"}
              </button>
            )}

            {form.role === 'nurse' && <>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Qualification</label>
                <input value={form.qualification} onChange={e => setForm({ ...form, qualification: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Uniform size</label>
                <select value={form.uniform_size} onChange={e => setForm({ ...form, uniform_size: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white outline-none">
                  <option value="">Not set</option>
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </>}

            {form.role === 'doctor' && <>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Medical license no.</label>
                <input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Hospital / clinic</label>
                <input value={form.hospital} onChange={e => setForm({ ...form, hospital: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Fellowship / qualifications</label>
                <input value={form.fellowship} onChange={e => setForm({ ...form, fellowship: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#311b52] block mb-1">Practice address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" />
              </div>
            </>}

            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" data-testid="save-staff-btn"
              className="w-full bg-[#673c96] text-white hover:bg-[#3e195e] rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
              {editId ? 'Update' : 'Create account'}
            </button>
            {!editId && (
              <p className="text-[11px] text-[#6b7280]">
                They sign in at {window.location.origin}/{form.role} with this email &amp; password, or with phone OTP. Password is emailed and SMS’d.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
